<template>
  <div class="admin-action-page">
    <CCard class="border-0 shadow-sm action-shell">
      <CCardBody class="p-4 p-lg-5">
        <div class="page-head">
            <div>
              <div class="eyebrow">Admin Action</div>
            <h1>Committee Review Routing</h1>
            <p>User submissions now go directly to committee review. This page shows the committee members who were notified.</p>
            </div>
          <div class="page-head__meta">
            <span class="meta-chip">{{ agreement ? agreement.documentCode : 'Loading...' }}</span>
            <span class="meta-chip meta-chip--status">{{ agreement ? stageLabel : 'Loading' }}</span>
          </div>
        </div>

        <div v-if="agreement" class="action-layout">
          <div class="action-main">
            <div class="surface-card">
              <div class="surface-title">Agreement Summary</div>
              <div class="detail-grid">
                <div class="detail-row"><span>Title</span><strong>{{ agreement.title }}</strong></div>
                <div class="detail-row"><span>Created by</span><strong>{{ agreement.createdBy }}</strong></div>
                <div class="detail-row"><span>Partner</span><strong>{{ agreement.partner }}</strong></div>
                <div class="detail-row"><span>Type</span><strong>{{ agreement.type }}</strong></div>
                <div class="detail-row"><span>Department</span><strong>{{ agreement.department }}</strong></div>
                <div class="detail-row"><span>Submitted on</span><strong>{{ formatDate(agreement.submittedAt) }}</strong></div>
                <div class="detail-row detail-row--wide"><span>Summary</span><strong>{{ agreement.summary || 'No summary provided.' }}</strong></div>
              </div>
            </div>

            <div class="surface-card">
              <div class="surface-title">Document Preview</div>
              <div class="pdf-sheet">
                <div class="pdf-topline">
                  <span>{{ previewAttachmentLabel }}</span>
                  <span>{{ agreement.documentCode }}</span>
                </div>
                <div v-if="previewLoading" class="empty-state">Loading Word draft from the database...</div>
                <div v-else-if="previewUrl" class="empty-state empty-state--doc">
                  <strong>Word document attached</strong>
                  <p>{{ previewAttachmentLabel }}</p>
                  <a :href="previewUrl" target="_blank" rel="noopener">Open document</a>
                </div>
                <div v-else-if="draftPreviewSections.length" class="draft-preview-stack">
                  <div v-for="section in draftPreviewSections" :key="section.key" class="draft-preview-card">
                    <div class="draft-preview-card__title">{{ section.title }}</div>
                    <div class="draft-preview-card__copy">{{ section.content }}</div>
                  </div>
                </div>
                <div v-else class="empty-state">
                  <strong>No Word document available</strong>
                  <p>{{ previewError || 'No attached DOC or DOCX was found, and the agreement draft content is also empty.' }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="action-side">
            <div class="surface-card">
              <div class="surface-title">Committee Members Notified</div>
              <div class="section-copy">Admin does not manually choose committee members here. The submitted document is routed to the committee review group automatically.</div>
              <div class="reviewer-grid">
                <div
                  v-for="reviewer in selectedReviewers"
                  :key="reviewer.id"
                  class="reviewer-option reviewer-option--active"
                >
                  <div class="reviewer-copy">
                    <strong>{{ reviewer.name }}</strong>
                    <span>{{ reviewer.role }}</span>
                    <small>{{ reviewer.department }}</small>
                  </div>
                </div>
              </div>
            </div>

            <div class="surface-card">
              <div class="surface-title">Assignment Summary</div>
              <div class="detail-grid compact">
                <div class="detail-row"><span>Committee members</span><strong>{{ selectedReviewers.length }}</strong></div>
                <div class="detail-row"><span>Current stage</span><strong>{{ stageLabel }}</strong></div>
              </div>
              <div v-if="selectedReviewers.length" class="assigned-list">
                <div v-for="reviewer in selectedReviewers" :key="reviewer.id" class="assigned-person">
                  <strong>{{ reviewer.name }}</strong>
                  <span>{{ reviewer.role }}</span>
                </div>
              </div>
              <div v-else class="empty-state empty-state--compact">Committee queue members will appear here once the document sync completes.</div>
            </div>
          </div>
        </div>

        <div v-else class="empty-state">Loading assignment workspace...</div>

        <div class="footer-actions">
          <CButton color="secondary" variant="outline" @click="$router.push('/review-queue')">Back to Queue</CButton>
          <CButton color="primary" :disabled="!agreement" @click="openReview">Open Review</CButton>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import api from '@/service/api'
import { isReadyForAdminFinalApproval } from '@/utils/review-final-approval'

export default {
  name: 'AdminAssignCommitteePage',
  data() {
    return {
      agreement: null,
      draftReviewerIds: [],
      previewAttachment: null,
      previewLoading: false,
      previewError: ''
    }
  },
  computed: {
    reviewId() {
      return String(this.$route.params.id || this.$route.query.agreementId || '')
    },
    stageLabel() {
      if (!this.agreement) return ''
      if (this.agreement.status === 'approved') return 'Approved'
      if (isReadyForAdminFinalApproval(this.agreement)) return 'Awaiting Final Approval'
      return {
        1: 'Draft Submitted',
        2: 'Committee Notified',
        3: 'Committee Review'
      }[Math.min(Number(this.agreement.currentStep || 1), 3)] || 'Draft Submitted'
    },
    selectedReviewers() {
      if (!(this.agreement && Array.isArray(this.agreement.availableReviewers))) return []
      if (this.draftReviewerIds.length) {
        return this.agreement.availableReviewers.filter(item => this.draftReviewerIds.includes(item.id))
      }
      return this.agreement.availableReviewers
    },
    canSubmit() {
      return !!this.agreement
    },
    previewUrl() {
      if (!this.previewAttachment) return ''
      return this.previewAttachment.url || this.previewAttachment.fileUrl || ''
    },
    previewAttachmentLabel() {
      if (!this.previewAttachment) return this.agreement ? this.agreement.fileLabel : 'MOU Draft'
      return this.previewAttachment.originalName || this.previewAttachment.fileName || (this.agreement && this.agreement.fileLabel) || 'MOU Draft'
    },
    draftPreviewSections() {
      return this.agreement && Array.isArray(this.agreement.draftPreviewSections)
        ? this.agreement.draftPreviewSections
        : []
    }
  },
  watch: {
    reviewId: {
      immediate: true,
      handler() {
        this.loadAgreement()
      }
    }
  },
  methods: {
    async loadAgreement() {
      if (!this.reviewId) {
        this.agreement = null
        return
      }
      const response = await api.adminReview('detail', { id: this.reviewId })
      let item = response && response.data && response.data.data ? response.data.data : null
      item = await this.ensureCommitteeRouting(item)
      this.agreement = item
      this.draftReviewerIds = this.agreement && Array.isArray(this.agreement.assignedReviewerIds)
        ? [...this.agreement.assignedReviewerIds]
        : []
      await this.loadPreviewAttachment()
    },
    async ensureCommitteeRouting(item) {
      if (!item || item.status === 'approved') return item
      if (Number(item.currentStep || 1) > 2) return item
      if (!Array.isArray(item.availableReviewers) || !item.availableReviewers.length) return item
      const assignedReviewerIds = item.availableReviewers.map(reviewer => reviewer.id)
      try {
        const response = await api.adminReview('update', {
          id: item.id,
          assignedReviewerIds,
          currentStep: 3,
          status: 'pending'
        })
        return response && response.data && response.data.data ? response.data.data : {
          ...item,
          assignedReviewerIds,
          committees: item.availableReviewers.map(reviewer => reviewer.name),
          currentStep: 3,
          status: 'pending'
        }
      } catch (error) {
        return {
          ...item,
          assignedReviewerIds,
          committees: item.availableReviewers.map(reviewer => reviewer.name),
          currentStep: 3,
          status: 'pending'
        }
      }
    },
    async loadPreviewAttachment() {
      if (!this.agreement) return
      this.previewLoading = true
      this.previewError = ''
      this.previewAttachment = null
      try {
        const response = await api.attachments('list', {
          ownerType: this.agreement.attachmentOwnerType || 'mou-review',
          ownerId: this.agreement.attachmentOwnerId || `mou-${this.agreement.id}`
        })
        const rawList = Array.isArray(response && response.data && response.data.data)
          ? response.data.data
          : Array.isArray(response && response.data) ? response.data : []
        const documentAttachment = rawList.find(item => {
          const mime = String(item.mimeType || '').toLowerCase()
          const name = String(item.originalName || item.fileName || '').toLowerCase()
          return mime.includes('msword') || mime.includes('wordprocessingml') || name.endsWith('.doc') || name.endsWith('.docx')
        }) || null
        this.previewAttachment = documentAttachment || this.buildAgreementDraftAttachment()
        if (!this.previewAttachment) this.previewError = 'No Word draft is attached for this MOU yet.'
      } catch (error) {
        this.previewAttachment = this.buildAgreementDraftAttachment()
        if (!this.previewAttachment && !this.draftPreviewSections.length) {
          this.previewError = 'Unable to load the MOU Word document from the database.'
        }
      } finally {
        this.previewLoading = false
      }
    },
    buildAgreementDraftAttachment() {
      const uploaded = this.agreement && this.agreement.uploadedDraft ? this.agreement.uploadedDraft : null
      if (uploaded && uploaded.url) {
        return {
          originalName: uploaded.name || this.agreement.fileLabel,
          fileName: uploaded.name || this.agreement.fileLabel,
          mimeType: uploaded.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          url: uploaded.url,
          fileUrl: uploaded.url
        }
      }
      return null
    },
    formatDate(value) {
      if (!value) return ''
      return new Date(value).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    assignedCommitteeAudienceKeys(item) {
      if (!(item && Array.isArray(item.availableReviewers) && Array.isArray(item.assignedReviewerIds))) return []
      return item.availableReviewers
        .filter(reviewer => item.assignedReviewerIds.includes(reviewer.id))
        .flatMap((reviewer) => ([
          reviewer && reviewer.email,
          reviewer && reviewer.id,
          reviewer && reviewer.department,
          reviewer && reviewer.role,
          reviewer && reviewer.name
        ]))
        .map(value => String(value || '').trim().toLowerCase())
        .filter(Boolean)
        .filter((value, index, rows) => rows.indexOf(value) === index)
    },
    openReview() {
      if (!this.agreement) return
      this.$router.push({
        path: '/review',
        query: { agreementId: this.agreement.id }
      })
    }
  }
}
</script>

<style scoped>
.admin-action-page {
  min-height: 100vh;
  padding: 24px;
}

.action-shell {
  border-radius: 22px;
}

.page-head {
  align-items: flex-start;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  margin-bottom: 20px;
}

.eyebrow {
  color: #9e212f;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.page-head h1 {
  color: #2d3748;
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 6px;
}

.page-head p,
.section-copy,
.empty-state,
.draft-preview-card__copy {
  color: #718096;
}

.page-head__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.meta-chip {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  color: #475569;
  font-weight: 700;
  padding: 10px 14px;
}

.meta-chip--status {
  background: #fff7ed;
  border-color: #fed7aa;
  color: #9a3412;
}

.action-layout {
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(0, 1.2fr) minmax(320px, 0.9fr);
}

.action-main,
.action-side {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.surface-card {
  background: #fafafa;
  border: 1px solid #edf2f7;
  border-radius: 18px;
  padding: 18px;
}

.surface-title {
  color: #2d3748;
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 14px;
}

.detail-grid {
  display: grid;
  gap: 12px;
}

.detail-grid.compact .detail-row {
  padding: 10px 0;
}

.detail-row {
  align-items: flex-start;
  border-bottom: 1px solid #edf2f7;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 12px 0;
}

.detail-row span {
  color: #718096;
}

.detail-row strong {
  color: #2d3748;
  max-width: 62%;
  text-align: right;
}

.detail-row--wide {
  align-items: flex-start;
  flex-direction: column;
}

.detail-row--wide strong {
  max-width: 100%;
  text-align: left;
}

.pdf-sheet {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 16px;
}

.pdf-topline {
  color: #718096;
  display: flex;
  font-size: 0.9rem;
  font-weight: 600;
  justify-content: space-between;
  margin-bottom: 14px;
}

.pdf-frame-wrap {
  min-height: 620px;
}

.pdf-frame {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  min-height: 620px;
  width: 100%;
}

.draft-preview-stack {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.draft-preview-card {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px;
}

.draft-preview-card__title {
  color: #2d3748;
  font-weight: 700;
  margin-bottom: 8px;
}

.reviewer-grid,
.assigned-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.reviewer-option {
  align-items: flex-start;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  cursor: pointer;
  display: flex;
  gap: 12px;
  padding: 14px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.reviewer-option--active {
  border-color: #f5a623;
  box-shadow: 0 10px 20px rgba(245, 166, 35, 0.16);
  transform: translateY(-1px);
}

.reviewer-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.reviewer-copy strong,
.assigned-person strong {
  color: #2d3748;
}

.reviewer-copy span,
.reviewer-copy small,
.assigned-person span {
  color: #718096;
}

.assigned-person {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
}

.empty-state {
  background: #fff;
  border: 1px dashed #d9e2ec;
  border-radius: 14px;
  padding: 18px;
  text-align: center;
}

.empty-state--compact {
  margin-top: 14px;
}

.footer-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 22px;
}

@media (max-width: 991px) {
  .action-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .admin-action-page {
    padding: 16px;
  }

  .page-head,
  .footer-actions,
  .pdf-topline,
  .detail-row {
    flex-direction: column;
  }

  .footer-actions {
    gap: 12px;
  }

  .detail-row strong {
    max-width: 100%;
    text-align: left;
  }
}
</style>
